import React, { useEffect, useRef, useState } from "react";
import { Crown, Sparkle } from "@phosphor-icons/react";
import api, { fileUrl } from "../lib/api";
import { frameStyle } from "../lib/badgeFrames";

const SPLASH_MS = 3800;

/**
 * LiveGiftSplash — polls /live/{id}/gifts and shows a full-width splash for
 * every new gift (with a "designed by @user" ribbon for marketplace gifts).
 * Admins additionally get the owner-cut ticker (platform_waves is only sent
 * to admins by the backend).
 */
export default function LiveGiftSplash({ sessionId, isAdmin = false }) {
  const [current, setCurrent] = useState(null);
  const [ownerTotal, setOwnerTotal] = useState(0);
  const [bump, setBump] = useState(false);
  const seen = useRef(null); // null until first fetch (old gifts are not replayed)
  const queue = useRef([]);
  const showing = useRef(false);

  useEffect(() => {
    let alive = true;
    const next = () => {
      if (!alive) return;
      const g = queue.current.shift();
      if (!g) { showing.current = false; setCurrent(null); return; }
      showing.current = true;
      setCurrent(g);
      setTimeout(next, SPLASH_MS);
    };
    const poll = async () => {
      try {
        const { data } = await api.get(`/live/${sessionId}/gifts`, { params: { limit: 15 } });
        const items = [...data].reverse(); // oldest → newest
        if (isAdmin) setOwnerTotal(items.reduce((a, g) => a + (g.platform_waves || 0), 0));
        if (seen.current === null) { seen.current = new Set(items.map((g) => g.id)); return; }
        const fresh = items.filter((g) => !seen.current.has(g.id));
        if (fresh.length) {
          fresh.forEach((g) => seen.current.add(g.id));
          if (seen.current.size > 500) seen.current = new Set([...seen.current].slice(-300));
          queue.current.push(...fresh.slice(-4));
          if (isAdmin && fresh.some((g) => g.platform_waves)) { setBump(true); setTimeout(() => setBump(false), 1200); }
          if (!showing.current) next();
        }
      } catch { /* stream may be ending */ }
    };
    poll();
    const t = setInterval(poll, 3000);
    return () => { alive = false; clearInterval(t); };
  }, [sessionId, isAdmin]);

  const fs = current ? frameStyle(current.sender_badge_frame) : null;
  return (
    <>
      {isAdmin && (
        <div className={`absolute top-[72px] right-4 z-30 crystal-glass rounded-full pl-2 pr-3 py-1.5 flex items-center gap-1.5 border transition-transform ${bump ? "scale-110 border-jam-gold" : "border-jam-gold/40"}`} data-testid="owner-cut-ticker">
          <Crown size={13} weight="fill" className="text-jam-gold" />
          <span className="text-cream text-[10px] font-black tracking-[0.15em] uppercase">You keep</span>
          <span className="text-jam-gold text-xs font-black tabular-nums" data-testid="owner-cut-total">{ownerTotal.toLocaleString()} 🪙</span>
          {bump && current?.platform_waves ? <span className="text-emerald-300 text-[10px] font-black" data-testid="owner-cut-bump">+{current.platform_waves}</span> : null}
        </div>
      )}
      {current && (
        <div className="absolute inset-x-0 top-[38%] z-30 flex justify-center pointer-events-none px-6" data-testid="live-gift-splash">
          <div key={current.id} className={`crystal-glass rounded-3xl px-6 py-4 text-center border max-w-xs w-full animate-gift-pop ${fs ? `${fs.border} ${fs.glow}` : "border-papaya/40"}`}>
            {current.gift_image_url ? (
              <img src={fileUrl(current.gift_image_url)} alt="" className="w-24 h-24 object-contain rounded-2xl mx-auto drop-shadow-[0_0_24px_rgba(56,189,248,0.6)]" />
            ) : (
              <div className="text-6xl leading-none">{current.gift_emoji}</div>
            )}
            <p className="text-cream text-sm mt-2"><b className={fs ? "text-jam-gold" : "text-papaya"}>@{current.sender_username}</b> sent <b>{current.gift_name}</b></p>
            <p className="text-cream/60 text-[10px] tracking-[0.2em] uppercase font-bold mt-0.5">{current.waves_spent} waves</p>
            {current.is_community && current.designer_username && (
              <div className="mt-2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-sky-400/20 border border-sky-300/40 text-sky-200 text-[10px] font-black tracking-[0.15em] uppercase" data-testid="live-gift-designer">
                <Sparkle size={11} weight="fill" /> designed by @{current.designer_username}
              </div>
            )}
            {isAdmin && current.platform_waves ? <p className="text-jam-gold text-[10px] font-black mt-1.5" data-testid="live-gift-owner-cut">owner cut +{current.platform_waves} 🪙</p> : null}
          </div>
        </div>
      )}
    </>
  );
}
